import {
  pulseConfigurationDefaultValues,
  PulseConfigurationKeys,
  PulseConfigurations,
  PULSE_FEATURE_NAME,
} from '@epicuro-next/constants/rule-engine/pulse-rules';
import { selectConfigurationValueFrom } from '@epicuro-next/platform/configuration';
import { memoize } from '@epicuro-next/utilities';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  Account,
  AccountCountry,
  AccountReviewer,
  AccountsQuery,
} from 'graphql-api/gql-api-interfaces';

import {
  AccountProperty,
  IAccountsQuerySearch,
  ISearchResult,
} from '../../types';
import * as fromAccounts from '../accounts.reducer';
import { ONCE_GET_PAGE_SIZE } from '../accounts.reducer';

export interface ApplicationState {
  [fromAccounts.accountsFeatureKey]: fromAccounts.State;
}

export const selectAccountsState = createFeatureSelector<fromAccounts.State>(
  fromAccounts.accountsFeatureKey,
);

export const {
  selectEntities: selectAccountEntities,
  selectAll: selectAccounts,
} = fromAccounts.adapter.getSelectors(selectAccountsState);

export const selectIsBESearchEnabledByRole = createSelector(
  selectAccountsState,
  (state) => state.isBESearchEnabled,
);

export const selectAccountsLoaded = createSelector<
  ApplicationState,
  [fromAccounts.State],
  boolean
>(selectAccountsState, (state) => state.loaded);

export const selectAllAccountPagesLoaded = createSelector(
  selectAccountsState,
  (state) => state.allAccountPagesLoaded,
);

export const selectAllBuyerAccountsLoaded = createSelector(
  selectAllAccountPagesLoaded,
  selectAccountsLoaded,
  (allAccountPagesLoaded, loaded) => loaded && allAccountPagesLoaded,
);

export const selectIsBeSearchEnabled = createSelector(
  selectIsBESearchEnabledByRole,
  selectAllBuyerAccountsLoaded,
  (isBESearchEnabled, allAccountsLoaded) =>
    isBESearchEnabled || !allAccountsLoaded,
);

export const selectAccountsIsLoading = createSelector<
  ApplicationState,
  [fromAccounts.State],
  boolean
>(selectAccountsState, (state) => state.loading);

export const selectActiveAccount = createSelector<
  ApplicationState,
  [fromAccounts.State],
  Account | null
>(selectAccountsState, (state) => state.activeAccount);

export const selectActiveAccountId = createSelector<
  ApplicationState,
  [Account | null],
  string | null
>(selectActiveAccount, (account) => {
  return account?.accountId || null;
});

export const selectActiveExternalAccountId = createSelector<
  ApplicationState,
  [Account | null],
  string | null
>(selectActiveAccount, (account) => {
  return account?.externalId || null;
});

export const selectActiveAccountBrandOrg = createSelector<
  ApplicationState,
  [Account | null],
  string | null
>(selectActiveAccount, (account) => {
  return account?.domainId || null;
});

export const selectActiveAccountBrandOrganizations = createSelector(
  selectActiveAccount,
  (account) => {
    return account?.brandOrganisations;
  },
);

export const selectActiveBrandId = createSelector(
  selectActiveAccountBrandOrganizations,
  (brandOrganizations) => {
    return brandOrganizations?.[0]?.brandId || null;
  },
);

export const selectAccountNowInventoryLeadTimeInDays = createSelector(
  selectActiveAccountBrandOrganizations,
  (brandOrganizations) => {
    try {
      const nowInventoryLeadTime = brandOrganizations?.find((bo) =>
        Boolean(bo?.nowInventoryLeadTime),
      )?.nowInventoryLeadTime;
      const days = (nowInventoryLeadTime ?? '0 days').split(' ')[0];

      return parseInt(days, 10) || 0;
    } catch (e) {
      console.error('Failed to parse nowInventoryLeadTime', e);
      return 0;
    }
  },
);

export const selectAccountCountries = createSelector(
  selectAccountsState,
  ({ availableCountries }): AccountCountry[] => availableCountries,
);

export const selectAccountReviewers = createSelector(
  selectAccountsState,
  ({ reviewers }): AccountReviewer[] => reviewers,
);

export const selectAccountReviewersLoading = createSelector(
  selectAccountsState,
  ({ reviewersLoading }): boolean => reviewersLoading,
);

export const selectAccountReviewersSearchQuery = createSelector(
  selectAccountsState,
  ({ reviewersSearchQuery }): string => reviewersSearchQuery,
);

export const selectFilteredAccountReviewers = createSelector(
  selectAccountReviewers,
  selectAccountReviewersSearchQuery,
  (reviewers, query) =>
    reviewers.filter(
      (reviewer) =>
        reviewer.displayName.includes(query) || reviewer.email.includes(query),
    ),
);

export const selectActiveAccountFeatures = createSelector(
  selectActiveAccount,
  (account) => account?.features || null,
);

export const selectActiveAccountProperties = createSelector(
  selectActiveAccount,
  (account) => account?.properties,
);

export const selectActiveAccountAddresses = createSelector(
  selectActiveAccount,
  (account) => account?.shipToAddresses,
);

export const selectAccountPropertyValue = memoize((key: AccountProperty) =>
  createSelector(
    selectActiveAccountProperties,
    (properties) =>
      (properties ?? []).find(
        (property: { key: string; value: string }) => property.key === key,
      )?.value,
  ),
);

export const selectCMIEnabledBrandsIds = createSelector(
  selectAccountPropertyValue(AccountProperty.isCMIEnabled),
  getBrandIdsFromAccountProperty,
);

export const selectVMIEnabledBrandsIds = createSelector(
  selectAccountPropertyValue(AccountProperty.isVMIEnabled),
  getBrandIdsFromAccountProperty,
);

export const selectAccountParkingReason = selectAccountPropertyValue(
  AccountProperty.parkingReason,
);
export const selectAccountParkingDate = createSelector(
  selectAccountPropertyValue(AccountProperty.parkingDate),
  (date) => {
    if (date) {
      // format dd-mm-yyyy to ISO format
      const [day, month, year] = date.split('-');
      return year.length === 4 ? `${year}-${month}-${day}` : date;
    }

    return date;
  },
);
export const selectAccountIncoterm = selectAccountPropertyValue(
  AccountProperty.incoTerms,
);

export const selectIsCMIRuleEnabled = selectConfigurationValueFrom<
  PulseConfigurationKeys.CMIEnabled,
  PulseConfigurations
>(
  PULSE_FEATURE_NAME,
  PulseConfigurationKeys.CMIEnabled,
  pulseConfigurationDefaultValues[PulseConfigurationKeys.CMIEnabled],
);

export const selectIsCMIEnabled = createSelector(
  selectCMIEnabledBrandsIds,
  selectIsCMIRuleEnabled,
  (brands, ruleLevel) => Boolean(brands.length && ruleLevel),
);

export const selectIsVmiRuleEnabled = selectConfigurationValueFrom<
  PulseConfigurationKeys.VMIEnabled,
  PulseConfigurations
>(
  PULSE_FEATURE_NAME,
  PulseConfigurationKeys.VMIEnabled,
  pulseConfigurationDefaultValues[PulseConfigurationKeys.VMIEnabled],
);

export const selectIsVMIEnabled = createSelector(
  selectVMIEnabledBrandsIds,
  selectIsVmiRuleEnabled,
  (brands, ruleLevel) => {
    return Boolean(brands.length && ruleLevel);
  },
);

export const selectActiveAccountDropShipAvailable = createSelector(
  selectActiveAccountFeatures,
  (features) => Boolean(features?.allowAlternativeAddress),
);

export const selectIsDropShipErpDriven = createSelector(
  selectActiveAccountFeatures,
  (features) => Boolean(features?.erpDrivenAlternativeDeliverAddress),
);

export const selectAccountsQuery = createSelector<
  ApplicationState,
  [fromAccounts.State],
  AccountsQuery
>(selectAccountsState, (state) => state.query);

export const selectAccountsQueryByUserRole = createSelector(
  selectAccountsQuery,
  selectIsBESearchEnabledByRole,
  (query, isBESearch) => {
    return {
      ...query,
      size: isBESearch ? query.size : ONCE_GET_PAGE_SIZE,
    };
  },
);

export const selectAccountsQuerySearch = createSelector<
  ApplicationState,
  [AccountsQuery],
  IAccountsQuerySearch
>(selectAccountsQueryByUserRole, (query) => query.query);

export const selectSearchResult = createSelector<
  ApplicationState,
  [fromAccounts.State],
  ISearchResult
>(selectAccountsState, (state) => state.searchResult);

export const selectPageWithoutSearch = createSelector(
  selectAccountsState,
  (state) => state.pageWithoutSearch,
);

export const selectAccountsQueryForRequest = createSelector(
  selectAccountsQueryByUserRole,
  selectPageWithoutSearch,
  (query, pageWithoutQuery) => {
    return {
      ...query,
      page: query.query ? query.page : pageWithoutQuery,
    };
  },
);

export const selectSearchResultList = createSelector<
  ApplicationState,
  [ISearchResult],
  Account[]
>(selectSearchResult, (result) => result.list);

export const selectSearchResultLoaded = createSelector(
  selectSearchResult,
  ({ loaded }) => loaded,
);

export const selectSearchResultListLength = createSelector(
  selectSearchResultList,
  (list) => list.length ?? 0,
);

export const selectCurrentAccountList = createSelector<
  ApplicationState,
  [IAccountsQuerySearch, Account[], Account[]],
  Account[]
>(
  selectAccountsQuerySearch,
  selectSearchResultList,
  selectAccounts,
  (search, searchList, fullList) => (search ? searchList : fullList),
);

export const selectNoSearchResult = createSelector(
  selectSearchResultLoaded,
  selectAccountsQuerySearch,
  selectSearchResultListLength,
  (loaded, search, total) => Boolean(loaded && search && total === 0),
);

export const selectAllSearchResultsLoaded = createSelector(
  selectSearchResult,
  (searchResult) => searchResult.allSearchAccountPagesLoaded,
);

export const selectAllAccountsLoaded = createSelector(
  selectAccountsQuerySearch,
  selectAllAccountPagesLoaded,
  selectAllSearchResultsLoaded,
  (search, accountPagesLoaded, allSearchLoaded) => {
    return search ? allSearchLoaded : accountPagesLoaded;
  },
);

export const selectActiveAccountCurrency = createSelector(
  selectActiveAccount,
  (activeAccount) => activeAccount?.currency ?? null,
);

function getBrandIdsFromAccountProperty(value: string): string[] {
  if (!value) return [];
  try {
    const brandIds = JSON.parse(value as unknown as string);
    return Array.isArray(brandIds) ? brandIds : [];
  } catch (e) {
    return [];
  }
}
